import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const adminApi = axios.create({
    baseURL: API,
    timeout: 15000,
    withCredentials: true,
    headers: { "Content-Type": "application/json" },
});

// If an authenticated request comes back 401 (e.g. the access-token cookie
// expired or was dropped in transit), try refreshing the session once via
// the httpOnly refresh_token cookie, then silently replay the original
// request. Only if the refresh itself fails do we surface the 401 (which
// triggers a real logout). This also covers the initial /auth/me session
// check on page load — a stale/expired access_token there should trigger a
// refresh attempt too, not an immediate logout. Only /auth/login and
// /auth/refresh themselves are excluded, to avoid infinite loops.
let refreshInFlight = null;

adminApi.interceptors.response.use(
    (res) => res,
    async (error) => {
        const { config, response } = error;
        const url = config?.url || "";
        const isAuthEndpoint = url.includes("/auth/login") || url.includes("/auth/refresh");

        if (response?.status === 401 && !isAuthEndpoint && !config._retriedAfterRefresh) {
            config._retriedAfterRefresh = true;
            try {
                if (!refreshInFlight) {
                    refreshInFlight = adminApi.post("/auth/refresh").finally(() => {
                        refreshInFlight = null;
                    });
                }
                await refreshInFlight;
                return adminApi(config);
            } catch (refreshErr) {
                return Promise.reject(error);
            }
        }
        return Promise.reject(error);
    }
);

export async function adminLogin(email, password) {
    const { data } = await adminApi.post("/auth/login", { email, password });
    return data;
}

export async function adminLogout() {
    await adminApi.post("/auth/logout");
}

export async function fetchCurrentAdmin() {
    const { data } = await adminApi.get("/auth/me");
    return data;
}

export async function fetchInquiries(params = {}) {
    const { data } = await adminApi.get("/inquiries", { params });
    return data;
}

export async function fetchAdminStats() {
    const { data } = await adminApi.get("/admin/stats");
    return data;
}

export async function updateInquiryStatus(id, status) {
    const { data } = await adminApi.patch(`/inquiries/${id}`, { status });
    return data;
}

export async function deleteInquiry(id) {
    await adminApi.delete(`/inquiries/${id}`);
    return true;
}

export async function bulkDeleteInquiries(ids) {
    const { data } = await adminApi.post("/inquiries/bulk-delete", { ids });
    return data;
}

export async function deleteAllInquiries() {
    const { data } = await adminApi.delete("/inquiries");
    return data;
}

export async function resendInquiryEmail(id) {
    const { data } = await adminApi.post(`/inquiries/${id}/resend-email`, {}, { timeout: 30000 });
    return data;
}
